import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PaymentReportItem } from "../../models/reports.model";
import { PaymentsReportTileComponent } from "../payments-report-tile/payments-report-tile.component";
import { DynamicListComponent } from "../dynamic-list/dynamic-list.component";
import { Pagination } from "../../services/pagination.service";
import { BehaviorSubject } from "rxjs";
import { shareReplay, take, tap } from "rxjs/operators";
import { ReportsService } from "../../services/reports.service";
import { BusinessService } from "../../services/business.service";
import { SupabaseService } from "../../services/supabase.service";
import { ViewAsService } from "../../services/view-as.service";
import { BusinessProfile } from "../../../../../common/src/lib/models/business-profile.model";
import { dateString } from "../../../../../common/src/lib/services";
import { limitStep } from "../../services/reports-service-extras";
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

function paymentItemsToPaginationList(items: PaymentReportItem[], kind: 'All' | 'Pending' | 'Paid') {
  return items.map(item => ({
    componentClass: PaymentsReportTileComponent,
    header: dateString(item.createdAt),
    args: { item, kind }
  }));
}

@Component({
  selector: 'app-payments-report-items',
  templateUrl: './payments-report-items.component.html',
  styleUrls: ['./payments-report-items.component.scss']
})
export class PaymentsReportItemsComponent implements OnInit, OnDestroy {

  @Input() current!: 'All' | 'Pending' | 'Paid';

  @ViewChild('allList') allList!: DynamicListComponent<PaymentsReportTileComponent>;
  @ViewChild('allDateFilterList') allDateFilterList!: DynamicListComponent<PaymentsReportTileComponent>;
  @ViewChild('pendingList') pendingList!: DynamicListComponent<PaymentsReportTileComponent>;
  @ViewChild('pendingDateFilterList') pendingDateFilterList!: DynamicListComponent<PaymentsReportTileComponent>;
  @ViewChild('paidList') paidList!: DynamicListComponent<PaymentsReportTileComponent>;
  @ViewChild('paidDateFilterList') paidDateFilterList!: DynamicListComponent<PaymentsReportTileComponent>;

  allPagination!: Pagination<PaymentReportItem>;
  allDateFilterPagination!: Pagination<PaymentReportItem>;
  pendingPagination!: Pagination<PaymentReportItem>;
  pendingDateFilterPagination!: Pagination<PaymentReportItem>;
  paidPagination!: Pagination<PaymentReportItem>;
  paidDateFilterPagination!: Pagination<PaymentReportItem>;

  loading$ = new BehaviorSubject<boolean>(true);

  dateFilter$ = this.reportsService.selectedDateRangeForEvent$.pipe(
    tap(async dates => {
      if (dates) {
        this.loadItems('All', 0, undefined, dates, true);
        this.loadItems('Pending', 0, undefined, dates, true);
        this.loadItems('Paid', 0, undefined, dates, true);
      } else {
        this.allDateFilterList?.clear();
        this.pendingDateFilterList?.clear();
        this.paidDateFilterList?.clear();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true})
  );

  constructor(
    private reportsService: ReportsService,
    private businessService: BusinessService,
    private supabaseService: SupabaseService,
    private viewAsService: ViewAsService,
  ) { }

  async ngOnInit() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    this.allPagination = this.createPagination(business.businessId, 'payments_all_report', 'p_all')
      .on(
        '*',
        'payment',
        _ => this.handleChange('All')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('All')
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('All')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.allList)
      );
    this.allDateFilterPagination = this.createPagination(business.businessId, 'payments_all_report', 'p_all_d')
      .on(
        '*',
        'payment',
        _ => this.handleChange('All', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('All', true)
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('All', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.allDateFilterList)
      );
    this.pendingPagination = this.createPagination(business.businessId, 'payments_pending_report', 'p_pen')
      .on(
        '*',
        'payment',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingList)
      );
    this.pendingDateFilterPagination = this.createPagination(business.businessId, 'payments_pending_report', 'p_pen_d')
      .on(
        '*',
        'payment',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingDateFilterList)
      );
    this.paidPagination = this.createPagination(business.businessId, 'payments_paid_report', 'p_paid')
      .on(
        '*',
        'payment',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidList)
      );
    this.paidDateFilterPagination = this.createPagination(business.businessId, 'payments_paid_report', 'p_paid_d')
      .on(
        '*',
        'payment',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidDateFilterList)
      );

    await this.loadItems('All', 0, business, undefined, false);
    await this.loadItems('Pending', 0, business, undefined, false);
    await this.loadItems('Paid', 0, business, undefined, false);
    this.loading$.next(false);
  }

  ngOnDestroy() {
    this.allPagination?.destroy();
    this.allDateFilterPagination?.destroy();
    this.pendingPagination?.destroy();
    this.pendingDateFilterPagination?.destroy();
    this.paidPagination?.destroy();
    this.paidDateFilterPagination?.destroy();
  }

  async loadItems(
    type: 'All' | 'Pending' | 'Paid',
    inOffset = 0,
    business?: BusinessProfile,
    dates?: [Date, Date] | null,
    dateList?: boolean,
    updateList?: boolean
  ) {
    if (!business)
      business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    if (dateList && !dates)
      dates = await this.dateFilter$.pipe(take(1)).toPromise();
    if (!dates)
      dates = [business.createdAt, new Date()];

    const selectedUsers = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();

    const pagination = type === 'Pending'
      ? !dateList
        ? this.pendingPagination
        : this.pendingDateFilterPagination
      : type === 'Paid'
        ? !dateList
          ? this.paidPagination
          : this.paidDateFilterPagination
        : !dateList
          ? this.allPagination
          : this.allDateFilterPagination;

    const list =
      type === 'Pending' ? !dateList
        ? this.pendingList
        : this.pendingDateFilterList
      : type === 'Paid'
        ? !dateList
          ? this.paidList
          : this.paidDateFilterList
        : !dateList
          ? this.allList
          : this.allDateFilterList;

    const inLimit = updateList && limitStep < list.getBottomCount ? list.getBottomCount : limitStep;

    const items = await pagination.get({
      startTime: dates[0],
      endTime: dates[1],
      rowLimit: inLimit,
      offsetCount: inOffset,
      selectedUsers: selectedUsers ?? []
    });

    if (items.length < inLimit && list.canLoadMoreBottom)
      list.canLoadMoreBottom = false;

    if (!inOffset)
      list.clear();
    list?.addAtBottom(paymentItemsToPaginationList(items, type));
  }

  async handleChange(type: string, dateList = false) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const dates = await this.dateFilter$.pipe(take(1)).toPromise();

    this.loadItems(
      type as 'All' | 'Pending' | 'Paid',
      0,
      business!,
      dateList ? dates : null,
      dateList,
      true
    );
  }

  handleWorkflowChange(payload: RealtimePostgresChangesPayload<any>, list: DynamicListComponent<PaymentsReportTileComponent>) {
    const data = payload.new;
    list.updateItems(component => {
      const report = component.instance.item;
      if (report.wId === data.id)
        report.jobType = data.jobType;
      return false;
    });
  }

  createPagination(businessId: string, fn: string, cname: string) {
    return new Pagination<PaymentReportItem>(
      this.supabaseService,
      fn,
      businessId,
      cname
    );
  }
}
